import { useRef } from 'react';
import { useLocalizedSpread } from '../../i18n/useLocalized';
import { useReadingFlow, hasPassed, hasReached, modesFor } from '../flow/useReadingFlow';
import { useFlowScroll } from '../flow/useFlowScroll';
import { useCardFlight } from '../flow/useCardFlight';
import FlowStep from '../flow/FlowStep';
import QuestionStep from '../question/QuestionStep';
import QuestionBanner from '../question/QuestionBanner';
import DeckSection from './DeckSection';
import SpreadActions from './SpreadActions';
import CardFanSelector from '../CardFanSelector';
import ReadingDisplay from '../ReadingDisplay';
import { ReadingSession } from '../../voice/ReadingSession';
import ReadingHalo from '../../voice/ReadingHalo';
import SingleCardSpread from './SingleCardSpread';
import ThreeCardSpread from './ThreeCardSpread';
import CelticCrossSpread from './CelticCrossSpread';
import RelationshipSpread from './RelationshipSpread';
import CareerSpread from './CareerSpread';

// Each spread's layout, and the class its stylesheet scopes the header
// and the cards under
const LAYOUTS = {
  'one-card': { Layout: SingleCardSpread, className: 'single-card-spread' },
  'three-card': { Layout: ThreeCardSpread, className: 'three-card-spread' },
  'celtic-cross': { Layout: CelticCrossSpread, className: 'celtic-cross-spread' },
  relationship: { Layout: RelationshipSpread, className: 'relationship-spread' },
  career: { Layout: CareerSpread, className: 'career-spread' }
};

/**
 * One reading of `spread`, as steps stacked down the page: ask the
 * question, shuffle the deck, pick the cards, turn them, read them. Each
 * step appears under the last and is locked once the reading moves past
 * it, so the reader can scroll back through it but not change it.
 *
 * The question is the exception: it is not a step but a thing the reading
 * carries, so it is on the page exactly once from the moment it is asked,
 * and travels from over the deck to over the cards (FlowStep.css sets the
 * two slots; QuestionBanner does the travelling).
 */
function SpreadFlow({ spread: rawSpread, onNewReading }) {
  const spread = useLocalizedSpread(rawSpread);
  const numCards = rawSpread.positions.length;
  const { Layout, className } = LAYOUTS[rawSpread.id];

  const flow = useReadingFlow(numCards);
  const { phase, mode, question, deck, isShuffling, hasShuffled, drawn, revealed, allRevealed } = flow;
  const reached = (step) => hasReached(phase, step);
  const passed = (step) => hasPassed(phase, step);

  // Where the question sits. It joins the cards as soon as they are all
  // picked; the page follows it there a beat later (useFlowScroll waits
  // for the fan to fold), so the two moves never fight.
  const questionAtCards = reached('reveal');

  const steps = {
    ask: useRef(null),
    shuffle: useRef(null),
    pick: useRef(null),
    reveal: useRef(null),
    reading: useRef(null)
  };
  useFlowScroll(phase, steps);
  // Where the deck sits, so the fan can pick it up from there
  const deckRef = useRef(null);

  // Each pick flies from its place in the fan to its place in the spread
  const { landingRef, launchFrom } = useCardFlight(drawn.length);
  const pick = (card, fanRect) => {
    launchFrom(fanRect);
    flow.pick(card);
  };

  const column = [
    'spread-flow',
    className,
    question ? 'has-question' : '',
    question && questionAtCards ? 'question-at-cards' : ''
  ].filter(Boolean).join(' ');

  return (
    // `drawn` is only handed over once asked for, which starts the reading
    <ReadingSession
      cards={reached('reading') ? drawn : null}
      spread={rawSpread}
      question={question}
      mode={mode ?? 'blank'}
    >
      <div className={column}>
        <FlowStep name="ask" ref={steps.ask} locked={passed('ask')}>
          <QuestionStep
            spreadName={spread.name}
            modes={modesFor(rawSpread.id)}
            mode={mode}
            question={question}
            onChooseMode={flow.chooseMode}
            onSubmit={flow.ask}
            locked={passed('ask')}
          />
        </FlowStep>

        {/* One element, two slots. Rendered here, between the step it was
            asked in and the deck; `order` carries it down to the cards.

            Not while the flow is back in the ask step, though: a question
            being reworded belongs in the field it is being typed in, and
            nowhere else. */}
        {question && reached('shuffle') && (
          <FlowStep name="question" className={questionAtCards ? 'is-at-cards' : ''}>
            <QuestionBanner
              question={question}
              mode={mode}
              atCards={questionAtCards}
              canEdit={phase === 'shuffle' && !drawn.length}
              onEdit={flow.reask}
            />
          </FlowStep>
        )}

        {reached('shuffle') && (
          <FlowStep name="shuffle" ref={steps.shuffle} locked={passed('shuffle')}>
            <DeckSection
              isShuffling={isShuffling}
              hasShuffled={hasShuffled}
              onShuffle={flow.shuffle}
              onDraw={flow.draw}
              numCards={numCards}
              deckRef={deckRef}
              locked={passed('shuffle')}
            />
          </FlowStep>
        )}

        {reached('pick') && (
          <FlowStep name="pick" ref={steps.pick} locked={passed('pick')}>
            <CardFanSelector
              deck={deck}
              numCards={numCards}
              pickedCount={drawn.length}
              deckRef={deckRef}
              onPick={pick}
              open={phase === 'pick'}
            />
          </FlowStep>
        )}

        {/* The spread fills while the reader picks: each card taken from the
            fan lands straight in its place here, and is the same card they
            then turn and have read — there is only ever the one */}
        {reached('pick') && (
          <FlowStep name="reveal" ref={steps.reveal} locked={passed('reveal')}>
            <ReadingHalo />
            <div ref={landingRef} className={`dealt-cards${phase === 'pick' ? ' is-filling' : ''}`}>
              <Layout
                spread={spread}
                cards={drawn}
                revealed={revealed}
                onReveal={flow.reveal}
              />
            </div>
            {reached('reveal') && (
              <SpreadActions
                allRevealed={allRevealed}
                drawnCardsLength={drawn.length}
                revealedCardsLength={revealed.length}
                onRevealAll={flow.revealAll}
                onGetReading={flow.requestReading}
                onNewReading={onNewReading}
                locked={passed('reveal')}
              />
            )}
          </FlowStep>
        )}
      </div>

      {reached('reading') && (
        <FlowStep name="reading" ref={steps.reading}>
          <ReadingDisplay onNewReading={onNewReading} />
        </FlowStep>
      )}
    </ReadingSession>
  );
}

export default SpreadFlow;
